// Renders a plugin's activity-bar container as a sidebar body: each declared
// `kind:'surface'` view becomes a sandboxed SurfaceHost iframe, and each
// `kind:'tree'` view becomes a host-rendered PluginTreeView whose rows launch
// the plugin's commands. All worker traffic stays host-mediated; surfaces never
// touch the host DOM (see SurfaceHost).

import { useEffect, useState, useSyncExternalStore, type ReactElement } from 'react';
import type { TreeItem } from '@slate/plugin-api';
import type { PluginHost } from './PluginHost';
import type { PluginSystem } from './setup';
import { SurfaceHost } from './surfaces/SurfaceHost';
import { desktopSurfaceSrc } from './surfaces/surfaceSrc';

const noop = (): void => {};

export interface PluginTreeViewProps {
  host: Pick<PluginHost, 'viewChildren' | 'runCommand'>;
  pluginId: string;
  viewId: string;
}

export function PluginTreeView({ host, pluginId, viewId }: PluginTreeViewProps): ReactElement {
  const [rows, setRows] = useState<TreeItem[]>([]);
  const [children, setChildren] = useState<Record<string, TreeItem[]>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    void host
      .viewChildren(pluginId, viewId)
      .then((r) => {
        if (!cancelled) setRows(r);
      })
      .catch(noop);
    return () => {
      cancelled = true;
    };
  }, [host, pluginId, viewId]);

  const onRow = (row: TreeItem): void => {
    if (row.collapsible) {
      setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(row.id)) {
          next.delete(row.id);
        } else {
          next.add(row.id);
          void host
            .viewChildren(pluginId, viewId, row.id)
            .then((c) => setChildren((m) => ({ ...m, [row.id]: c })))
            .catch(noop);
        }
        return next;
      });
      return;
    }
    void host.runCommand(pluginId, row.id).catch(noop);
  };

  return (
    <ul className="mp-plugin-tree" role="tree" aria-label="Plugin view">
      {rows.map((row) => (
        <li key={row.id} role="none">
          <button
            type="button"
            role="treeitem"
            className="mp-plugin-tree__row"
            aria-expanded={row.collapsible ? expanded.has(row.id) : undefined}
            onClick={() => onRow(row)}
          >
            {row.label}
          </button>
          {row.collapsible && expanded.has(row.id) && (
            <ul className="mp-plugin-tree mp-plugin-tree--nested" role="group">
              {(children[row.id] ?? []).map((c) => (
                <li key={c.id} role="none">
                  <button
                    type="button"
                    role="treeitem"
                    className="mp-plugin-tree__row"
                    onClick={() => void host.runCommand(pluginId, c.id).catch(noop)}
                  >
                    {c.label}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ul>
  );
}

export interface PluginActivityViewProps {
  system: PluginSystem;
  pluginId: string;
  containerId: string;
}

export function PluginActivityView({
  system,
  pluginId,
  containerId,
}: PluginActivityViewProps): ReactElement {
  const host = system.host;
  // Re-derive when the declarative-contribution set changes (install/enable/
  // disable), so a view added or removed at runtime appears/disappears.
  useSyncExternalStore(
    host.onDidChangeContributions.bind(host),
    host.contributionVersion.bind(host),
    host.contributionVersion.bind(host),
  );

  const views = host
    .listViews()
    .filter((v) => v.pluginId === pluginId && v.container === containerId);
  const surfaces = host.listSurfaces();

  return (
    <div className="mp-plugin-view">
      {views.map((v) => {
        if (v.kind === 'surface') {
          const surface = surfaces.find((s) => s.pluginId === pluginId && s.id === v.id);
          if (!surface) return null;
          return (
            <div key={v.id} className="mp-plugin-view__surface">
              <SurfaceHost
                system={system}
                pluginId={pluginId}
                surfaceId={v.id}
                src={desktopSurfaceSrc(pluginId, surface.path)}
                title={v.title}
              />
            </div>
          );
        }
        return (
          <div key={v.id} className="mp-plugin-view__tree">
            <h3 className="mp-plugin-view__title">{v.title}</h3>
            <PluginTreeView host={host} pluginId={pluginId} viewId={v.id} />
          </div>
        );
      })}
    </div>
  );
}
